import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { useApp } from "../context/AppContext";
import { ZoomIn, ZoomOut } from "lucide-react";

// Square viewport size (px). The inscribed circle previews the EXACT final crop
// (avatars render as circles everywhere in the app).
const VIEW = 280;
const OUT_SIZE = 384; // exported JPEG side
const MAX_ZOOM = 3;

/**
 * WhatsApp-style circular avatar cropper:
 * - drag to reposition (mouse + touch via pointer events)
 * - zoom slider (keeps the visible center anchored)
 * - live circular preview; confirm exports a 384x384 JPEG (base64, no prefix)
 */
export function AvatarCropDialog({ open, file, onClose, onCropped, title, mode = "avatar", confirmLabel }) {
  const sticker = mode === "sticker";
  const { t } = useApp();
  const [imgUrl, setImgUrl] = useState(null);
  const imgRef = useRef(null); // loaded HTMLImageElement
  const [nat, setNat] = useState(null); // natural {w, h}
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 }); // top-left of scaled image, relative to viewport
  const dragRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const baseScale = nat ? VIEW / Math.min(nat.w, nat.h) : 1; // "cover" scale
  const scale = baseScale * zoom;

  // Load the picked file whenever the dialog opens
  useEffect(() => {
    if (!open || !file) return undefined;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const b = VIEW / Math.min(img.width, img.height);
      setNat({ w: img.width, h: img.height });
      setZoom(1);
      setPos({ x: (VIEW - img.width * b) / 2, y: (VIEW - img.height * b) / 2 }); // centered
      setImgUrl(url);
    };
    img.onerror = () => onClose?.();
    img.src = url;
    return () => {
      URL.revokeObjectURL(url);
      setImgUrl(null);
      imgRef.current = null;
      setNat(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file]);

  // Keep the image always covering the viewport
  const clampPos = (x, y, s) => ({
    x: Math.min(0, Math.max(VIEW - (nat?.w || 0) * s, x)),
    y: Math.min(0, Math.max(VIEW - (nat?.h || 0) * s, y)),
  });

  const applyZoom = (z) => {
    if (!nat) return;
    const s0 = baseScale * zoom;
    const s1 = baseScale * z;
    // anchor: the source point currently at the viewport center stays put
    const cx = (VIEW / 2 - pos.x) / s0;
    const cy = (VIEW / 2 - pos.y) / s0;
    setPos(clampPos(VIEW / 2 - cx * s1, VIEW / 2 - cy * s1, s1));
    setZoom(z);
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onPointerMove = (e) => {
    if (!dragRef.current || !nat) return;
    setPos(clampPos(
      dragRef.current.ox + (e.clientX - dragRef.current.sx),
      dragRef.current.oy + (e.clientY - dragRef.current.sy),
      scale,
    ));
  };
  const endDrag = () => { dragRef.current = null; };

  const confirmCrop = () => {
    const img = imgRef.current;
    if (!img || !nat) return;
    setBusy(true);
    try {
      const sx = -pos.x / scale;
      const sy = -pos.y / scale;
      const sSize = VIEW / scale;
      const out = sticker ? 512 : OUT_SIZE;
      const canvas = document.createElement("canvas");
      canvas.width = out;
      canvas.height = out;
      const ctx = canvas.getContext("2d");
      if (sticker) {
        // rounded-corner sticker with transparent corners
        const r = out * 0.12;
        ctx.beginPath();
        ctx.moveTo(r, 0); ctx.arcTo(out, 0, out, out, r); ctx.arcTo(out, out, 0, out, r); ctx.arcTo(0, out, 0, 0, r); ctx.arcTo(0, 0, out, 0, r);
        ctx.closePath(); ctx.clip();
      }
      ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, out, out);
      const b64 = (sticker ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.85)).split(",")[1];
      onCropped?.(b64);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="avatar-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title || t("adjust_photo")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <div
            className="relative overflow-hidden rounded-xl bg-neutral-950 select-none cursor-grab active:cursor-grabbing"
            style={{ width: VIEW, height: VIEW, touchAction: "none" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            data-testid="avatar-crop-viewport"
          >
            {imgUrl && nat && (
              <img
                src={imgUrl}
                alt=""
                draggable={false}
                className="absolute max-w-none pointer-events-none"
                style={{ left: pos.x, top: pos.y, width: nat.w * scale, height: nat.h * scale }}
              />
            )}
            {/* Circular mask — everything outside the circle is dimmed */}
            <div className="absolute inset-0 pointer-events-none">
              <div
                className={`absolute inset-0 border-2 border-white/90 ${sticker ? "rounded-[12%]" : "rounded-full"}`}
                style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)" }}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center px-2" data-testid="avatar-crop-hint">
            {t(sticker ? "crop_hint_sticker" : "crop_hint")}
          </p>
          <div className="flex items-center gap-3 w-full px-1">
            <ZoomOut className="h-4 w-4 text-muted-foreground shrink-0" />
            <Slider
              value={[zoom]}
              min={1}
              max={MAX_ZOOM}
              step={0.01}
              onValueChange={([z]) => applyZoom(z)}
              className="flex-1"
              data-testid="avatar-crop-zoom"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} data-testid="avatar-crop-cancel">
            {t("cancel")}
          </Button>
          <Button onClick={confirmCrop} disabled={busy || !imgUrl} data-testid="avatar-crop-confirm">
            {confirmLabel || t("use_photo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
